import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Flex, Grid, HStack, IconButton, Text, Divider, VStack, } from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { useMemo, useState } from "react";
import { roundToNearest60 } from "../../shared-utils/utils";
import { addMinutes, format, parse } from "date-fns";
import { isTimeSlotWithinBuffer } from "../../helpers/bookingAvailability";
const formatDate = (dt) => dt.toLocaleDateString("en-US", {
    weekday: "short",
    day: "2-digit",
    month: "short",
});
const formatTime = (dt) => dt.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
});
const SelectPaxAndTime = ({ event, selectedSubEventId, initialMale = 0, initialFemale = 0, initialSelectedTime, initialSelectedSlotId, onConfirm, onCancel, hidePaxSelection = false, }) => {
    const [male, setMale] = useState(initialMale);
    const [female, setFemale] = useState(initialFemale);
    const [selectedSlot, setSelectedSlot] = useState(initialSelectedTime
        ? (() => {
            const dt = new Date(initialSelectedTime.replace("_", " "));
            if (isNaN(dt.getTime()))
                return null;
            return {
                date: format(dt, "EEE, MMM dd"),
                time: format(dt, "h:mm a"),
                slotId: initialSelectedSlotId || 0,
            };
        })()
        : null);
    const totalPax = male + female;
    const isConfirmDisabled = hidePaxSelection
        ? !selectedSlot
        : (event === null || event === void 0 ? void 0 : event.buffer_time_minutes) &&
            event.buffer_time_minutes > 0 &&
            !event.is_table_booking_enabled
            ? !selectedSlot
            : totalPax === 0 || !selectedSlot;
    const generateTimeSlots = (slot, id) => {
        const slotStartDate = new Date(String(slot.start_time));
        const currentTime = new Date();
        const startDate = slotStartDate < currentTime ? currentTime : slotStartDate;
        const endDate = new Date(String(slot.end_time));
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()))
            return [];
        const timeSlots = [];
        let roundedTime = roundToNearest60(startDate);
        // Get buffer time from event
        const bufferMinutes = (event === null || event === void 0 ? void 0 : event.buffer_time_minutes) || 0;
        while (roundedTime <= endDate) {
            // Check if this time slot is within buffer time
            if (isTimeSlotWithinBuffer(roundedTime, bufferMinutes)) {
                timeSlots.push({
                    time: format(roundedTime, "h:mm a"),
                    date: format(roundedTime, "yyyy/MM/dd"),
                    slotId: id,
                });
            }
            roundedTime = addMinutes(roundedTime, 60);
        }
        return timeSlots;
    };
    const slotsGroupedByDate = useMemo(() => {
        var _a, _b, _c, _d;
        if (!event)
            return {};
        const map = {};
        const pushSlot = (slot) => {
            var _a;
            // Skip slots that have ended
            const slotEndTime = new Date(slot.end_time);
            if (slotEndTime < new Date()) {
                return;
            }
            const isGuestSlot = ((_a = slot === null || slot === void 0 ? void 0 : slot.direct_passes) === null || _a === void 0 ? void 0 : _a.length) > 0 &&
                (() => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
                    const ladiesPass = slot.direct_passes.find((dp) => (dp === null || dp === void 0 ? void 0 : dp.pass_type) === "LADIES");
                    const couplePass = slot.direct_passes.find((dp) => (dp === null || dp === void 0 ? void 0 : dp.pass_type) === "COUPLE");
                    if (!ladiesPass || !couplePass)
                        return false;
                    const ladiesCoverAmount = ((_b = (_a = ladiesPass === null || ladiesPass === void 0 ? void 0 : ladiesPass.booking_prices) === null || _a === void 0 ? void 0 : _a.cover) !== null && _b !== void 0 ? _b : 0) -
                        ((_d = (_c = ladiesPass === null || ladiesPass === void 0 ? void 0 : ladiesPass.booking_prices) === null || _c === void 0 ? void 0 : _c.cover_discount) !== null && _d !== void 0 ? _d : 0);
                    const ladiesEntryAmount = ((_f = (_e = ladiesPass === null || ladiesPass === void 0 ? void 0 : ladiesPass.booking_prices) === null || _e === void 0 ? void 0 : _e.entry) !== null && _f !== void 0 ? _f : 0) -
                        ((_h = (_g = ladiesPass === null || ladiesPass === void 0 ? void 0 : ladiesPass.booking_prices) === null || _g === void 0 ? void 0 : _g.entry_discount) !== null && _h !== void 0 ? _h : 0);
                    const coupleCoverAmount = ((_k = (_j = couplePass === null || couplePass === void 0 ? void 0 : couplePass.booking_prices) === null || _j === void 0 ? void 0 : _j.cover) !== null && _k !== void 0 ? _k : 0) -
                        ((_m = (_l = couplePass === null || couplePass === void 0 ? void 0 : couplePass.booking_prices) === null || _l === void 0 ? void 0 : _l.cover_discount) !== null && _m !== void 0 ? _m : 0);
                    const coupleEntryAmount = ((_p = (_o = couplePass === null || couplePass === void 0 ? void 0 : couplePass.booking_prices) === null || _o === void 0 ? void 0 : _o.entry) !== null && _p !== void 0 ? _p : 0) -
                        ((_r = (_q = couplePass === null || couplePass === void 0 ? void 0 : couplePass.booking_prices) === null || _q === void 0 ? void 0 : _q.entry_discount) !== null && _r !== void 0 ? _r : 0);
                    return (ladiesCoverAmount === 0 &&
                        ladiesEntryAmount === 0 &&
                        coupleCoverAmount === 0 &&
                        coupleEntryAmount === 0);
                })();
            const timeSlots = generateTimeSlots(slot, slot.id);
            timeSlots.forEach((t) => {
                const dateKey = formatDate(new Date(t.date));
                if (!map[dateKey])
                    map[dateKey] = [];
                const slotValue = `${t.date}_${t.time}`;
                const existingIndex = map[dateKey].findIndex((item) => item.value === slotValue);
                // Parse time for sorting - combine date and time to create full datetime
                const fullDateTimeStr = `${t.date} ${t.time}`;
                const parsedDateTime = parse(fullDateTimeStr, "yyyy/MM/dd h:mm a", new Date());
                const timeForSorting = format(parsedDateTime, "HH:mm");
                const newSlotItem = {
                    label: t.time,
                    value: slotValue,
                    date: dateKey,
                    slotId: t.slotId,
                    sortableDate: t.date,
                    sortableTime: timeForSorting,
                    isGuestSlot: isGuestSlot,
                };
                if (existingIndex >= 0) {
                    // If duplicate exists, keep the one with higher slotId
                    if (t.slotId > map[dateKey][existingIndex].slotId) {
                        map[dateKey][existingIndex] = newSlotItem;
                    }
                }
                else {
                    // No duplicate, add the slot
                    map[dateKey].push(newSlotItem);
                }
            });
        };
        // If selectedSubEventId is provided, only show slots from that sub-event
        if (selectedSubEventId) {
            const selectedSubEvent = (_a = event.sub_events) === null || _a === void 0 ? void 0 : _a.find((se) => se.id === selectedSubEventId);
            if (selectedSubEvent) {
                (_b = selectedSubEvent.slots) === null || _b === void 0 ? void 0 : _b.forEach((slot) => pushSlot(slot));
            }
        }
        else {
            // Otherwise, show all slots (direct_slots + all sub_events slots)
            (_c = event.direct_slots) === null || _c === void 0 ? void 0 : _c.forEach((slot) => pushSlot(slot));
            (_d = event.sub_events) === null || _d === void 0 ? void 0 : _d.forEach((se) => { var _a; return (_a = se.slots) === null || _a === void 0 ? void 0 : _a.forEach((slot) => pushSlot(slot)); });
        }
        // Sort slots within each date group by time
        Object.keys(map).forEach((dateKey) => {
            map[dateKey].sort((a, b) => {
                // Sort by time within the same date group
                return a.sortableTime.localeCompare(b.sortableTime);
            });
        });
        return map;
    }, [event, selectedSubEventId]);
    const hasGuestSlots = useMemo(() => Object.values(slotsGroupedByDate).some((slots) => slots.some((slot) => slot.isGuestSlot)), [slotsGroupedByDate]);
    const decrease = (val, setter) => {
        if (val > 0)
            setter(val - 1);
    };
    const increase = (val, setter) => setter(val + 1);
    const Counter = ({ label, value, setValue, }) => (_jsxs(Flex, { justify: "space-between", align: "center", w: "100%", bg: "transparent", children: [_jsx(Text, { fontSize: "14px", fontWeight: "400", color: "white", children: label }), _jsxs(HStack, { spacing: 3, bg: "transparent", children: [_jsx(IconButton, { borderWidth: "1px", borderColor: "#4E4E4E", borderRadius: "100px", "aria-label": "minus", icon: _jsx(MinusIcon, { color: "white", boxSize: 3 }), size: "sm", bg: "transparent", _hover: { bg: "gray.600" }, onClick: () => decrease(value, setValue), gap: "6px", color: "#FFFFFF" }), _jsx(Text, { color: "white", w: "14px", fontWeight: "700", textAlign: "center", children: value }), _jsx(IconButton, { borderWidth: "1px", borderColor: "#4E4E4E", borderRadius: "100px", "aria-label": "plus", icon: _jsx(AddIcon, { color: "white", boxSize: 3 }), size: "sm", bg: "transparent", _hover: { bg: "gray.600" }, onClick: () => increase(value, setValue), gap: "6px", color: "#FFFFFF" })] })] }));
    return (_jsxs(VStack, { w: "100%", bg: "#1E1E1E", borderRadius: "16px", p: 5, color: "white", spacing: 4, children: [_jsxs(VStack, { w: "100%", flex: "1", overflowY: "auto", spacing: 5, pb: 24, children: [!hidePaxSelection &&
                        !((event === null || event === void 0 ? void 0 : event.buffer_time_minutes) &&
                            event.buffer_time_minutes > 0 &&
                            !event.is_table_booking_enabled) && (_jsxs(VStack, { w: "100%", spacing: 4, bg: "#151515ff", p: 4, borderRadius: "16px", children: [_jsx(Text, { w: "100%", textAlign: "left", fontSize: "12px", fontWeight: "400", color: "#FFFFFF", children: "How Many Are In?" }), _jsxs(VStack, { w: "100%", spacing: 2, children: [_jsx(Counter, { label: "Male", value: male, setValue: setMale }), _jsx(Divider, { borderColor: "#2F2F2F" }), _jsx(Counter, { label: "Female", value: female, setValue: setFemale })] })] })), _jsxs(VStack, { spacing: 5, w: "100%", bg: "#151515ff", p: 4, borderRadius: "16px", children: [_jsx(Text, { w: "100%", textAlign: "left", fontSize: "12px", fontWeight: "400", color: "#FFFFFF", children: "Select Your Visiting Time" }), hasGuestSlots ? (_jsxs(HStack, { w: "100%", spacing: 2, alignItems: "center", children: [_jsx(Box, { w: "6px", h: "6px", borderRadius: "full", bg: "#22C55E" }), _jsx(Text, { fontSize: "11px", fontWeight: "400", color: "#9A9A9A", children: "Guest list" })] })) : null, Object.entries(slotsGroupedByDate)
                                .sort(([dateA, slotsA], [dateB, slotsB]) => {
                                // Sort dates chronologically by comparing the first slot's sortableDate
                                if (slotsA.length === 0 && slotsB.length === 0)
                                    return 0;
                                if (slotsA.length === 0)
                                    return 1;
                                if (slotsB.length === 0)
                                    return -1;
                                return slotsA[0].sortableDate.localeCompare(slotsB[0].sortableDate);
                            })
                                .map(([date, slots]) => (_jsxs(VStack, { w: "100%", spacing: 3, children: [_jsx(Text, { alignSelf: "flex-start", w: "100%", textAlign: "left", fontSize: "12px", fontWeight: "400", color: "#FFFFFF", children: date }), _jsx(Grid, { templateColumns: "repeat(3,1fr)", gap: 3, w: "100%", children: slots.map((s) => {
                                            const isActive = (selectedSlot === null || selectedSlot === void 0 ? void 0 : selectedSlot.date) === s.date &&
                                                (selectedSlot === null || selectedSlot === void 0 ? void 0 : selectedSlot.time) === s.label;
                                            const isGuest = s.isGuestSlot;
                                            return (_jsxs(Box, { position: "relative", w: "100%", children: [_jsx(Box, { cursor: "pointer", h: "44px", w: "100%", display: "flex", alignItems: "center", justifyContent: "center", py: 2, px: 2, textAlign: "center", borderRadius: "10px", border: "1px solid", fontSize: "12px", fontWeight: "400", borderColor: isActive ? "purple.500" : "#2F2F2F", bg: isActive ? "#8155FE" : "transparent", onClick: () => setSelectedSlot({
                                                            date: s.date,
                                                            time: s.label,
                                                            slotId: s.slotId,
                                                            selectedDateTime: s.value,
                                                        }), _hover: { borderColor: "purple.400" }, _focus: { outline: "none", boxShadow: "none" }, _active: { outline: "none" }, transition: "all 0.2s ease", children: _jsx(Text, { children: s.label }) }), isGuest && (_jsx(Box, { position: "absolute", top: "6px", right: "6px", w: "6px", h: "6px", borderRadius: "full", bg: "#22C55E", zIndex: 2, pointerEvents: "none" }))] }, s.value));
                                        }) })] }, date)))] })] }), _jsxs(Flex, { position: "fixed", bottom: 0, left: 0, right: 0, bg: "#1E1E1E", pt: 4, pb: 3, px: 5, w: "100%", gap: [3, 3, 3, 6], alignItems: "center", justifyContent: "center", zIndex: 1000, children: [_jsx(Button, { variant: "outline", w: "100%", borderColor: "#4E4E4E", _hover: { bg: "#1c1c1cff" }, bg: "#1c1c1cff", fontSize: "12px", fontWeight: 400, onClick: onCancel, maxW: "300px", color: "white", children: "Cancel" }), _jsx(Button, { w: "100%", bg: "#8155FE", fontSize: "12px", fontWeight: 400, maxW: "300px", _hover: { bg: "#8155FE" }, isDisabled: isConfirmDisabled, color: "white", onClick: () => {
                            if (!selectedSlot)
                                return;
                            const datetime = selectedSlot.selectedDateTime
                                ? new Date(selectedSlot.selectedDateTime.replace("_", " "))
                                : new Date(`${selectedSlot.date} ${selectedSlot.time}`);
                            onConfirm({
                                counts: { male, female },
                                selectedDate: format(datetime, "yyyy-MM-dd"),
                                selectedTime: format(datetime, "HH:mm:ss"),
                                slotId: selectedSlot.slotId,
                            });
                        }, children: "Confirm Reservation" })] })] }));
};
export default SelectPaxAndTime;
